import { useEffect, useState } from 'react'

import { Calendar01Icon, Clock01Icon, Location01Icon, UserCircleIcon } from '@hugeicons/react'
import clsx from 'clsx'
import { DateTime } from 'luxon'

import { useResourceRecordsContext } from '@/shared/hooks'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Badge } from '@/shared/ui/Badge'
import type { BadgeStatusVariant, BadgeTone } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { HeartReactionButton } from '@/shared/ui/Reactions/HeartReactionButton'

import { getProgrammingKind } from '../model/kind'
import type { ProgrammingKindTone } from '../model/kind'
import { canCheckInForSession, getTemporalStatus } from '../model/temporal'
import type { Session, TemporalStatus } from '../model/types'

import { RewardPill } from '@/shared/ui/RewardPill'

/** Variante do `Badge` para o tipo de sessão (rótulo ghost). */
const KIND_BADGE_VARIANT: Record<ProgrammingKindTone, BadgeStatusVariant> = {
  brand: 'available',
  warning: 'progress',
  info: 'available',
  success: 'success',
  neutral: 'locked'
}

type StatusBadge = {
  variant: BadgeStatusVariant
  tone?: BadgeTone
  labelKey: string
}

const statusBadgeFor = (
  status: TemporalStatus,
  finished?: boolean
): StatusBadge => {
  if (finished) return { variant: 'success', labelKey: 'schedule.status.checkedIn' }
  if (status === 'live') return { variant: 'progress', labelKey: 'schedule.status.live' }
  if (status === 'past') return { variant: 'locked', labelKey: 'schedule.status.done' }
  return { variant: 'available', tone: 'light', labelKey: 'schedule.status.startsIn' }
}

const SpeakerStack = ({ speakers }: { speakers?: Session['speakers'] }) => {
  const { t } = useTranslation()
  if (!speakers?.length) return null

  const visible = speakers.slice(0, 2)
  const extra = speakers.length - visible.length

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex -space-x-1.5" aria-hidden>
        {visible.map((speaker, index) => (
          <div
            key={speaker?.id ?? index}
            className="size-5 shrink-0 overflow-hidden rounded-full border border-white bg-gray-200"
          >
            {speaker?.photo?.file?.url ? (
              <img
                src={speaker.photo.file.url}
                alt=""
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center">
                <UserCircleIcon size={10} />
              </div>
            )}
          </div>
        ))}
      </div>
      <span className="text-2xs leading-none text-gray-700">
        {visible.map(speaker => speaker?.name).filter(Boolean).join(', ')}
        {extra > 0 ? ` ${t('schedule.speakers.more', { count: extra })}` : ''}
      </span>
    </div>
  )
}

const startsInLabel = (startAt: string) => {
  const diff = DateTime.fromISO(startAt).diffNow(['hours', 'minutes'])
  const hours = Math.floor(diff.hours)
  const minutes = Math.floor(diff.minutes)

  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 1) return `${minutes} min`
  return null
}

export type ScheduleSessionCardProps = {
  session: Session
  /**
   * Abre a sessão no ponto em que o check-in acontece. Já foi a câmera de QR;
   * com a emenda GO!RN é a folha de detalhe, onde a palavra é digitada.
   */
  onStartCheckIn: (session: Session) => void
  onClick?: (session: Session) => void
}

export const ScheduleSessionCard = ({
  session,
  onStartCheckIn,
  onClick
}: ScheduleSessionCardProps) => {
  const { t } = useTranslation()
  const { refetch } = useResourceRecordsContext() as { refetch?: () => void }

  const [localReactions, setLocalReactions] = useState(session.reactions)
  const [userReaction, setUserReaction] = useState(() =>
    session.my_reactions?.includes?.('like') ? 'like' : null
  )

  useEffect(() => {
    setLocalReactions(session.reactions)
  }, [session.reactions])

  useEffect(() => {
    setUserReaction(session.my_reactions?.includes?.('like') ? 'like' : null)
  }, [session.my_reactions])

  const status = getTemporalStatus(session.start_at, session.end_at)
  const kind = getProgrammingKind(session.kind)

  const badge = statusBadgeFor(status, session.finished)
  const startsIn = startsInLabel(session.start_at)
  const badgeLabel =
    badge.labelKey === 'schedule.status.startsIn'
      ? startsIn
        ? t('schedule.status.startsIn', { time: startsIn })
        : t('schedule.status.soon')
      : t(badge.labelKey)

  const timeLabel = `${DateTime.fromISO(session.start_at).toFormat('HH:mm')} — ${DateTime.fromISO(session.end_at).toFormat('HH:mm')}`
  const canCheckIn = canCheckInForSession(session)

  const handleReactionChange = (
    newCounts: Record<string, number>,
    newUserReaction: string | null
  ) => {
    setLocalReactions(newCounts)
    setUserReaction(newUserReaction)
    refetch?.()
  }

  const handleCheckIn = event => {
    event.stopPropagation()
    onStartCheckIn(session)
  }

  return (
    <Card
      onClick={onClick ? () => onClick(session) : undefined}
      className={clsx(
        'card-gamification cursor-pointer shadow-sm transition-shadow hover:shadow-md',
        status === 'live' && 'border-2 border-brand/30',
        status === 'past' && 'opacity-90'
      )}
    >
      <Card.Body>
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-brand/10 bg-brand-light">
            <Calendar01Icon
              size={16}
              variant="stroke"
              type="rounded"
              className="text-gray-900"
              aria-hidden
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge
                variant={KIND_BADGE_VARIANT[kind.tone]}
                tone="ghost"
                size="sm"
                showIcon={false}
              >
                {t(kind.nameKey)}
              </Badge>
              <Badge variant={badge.variant} tone={badge.tone} size="sm" showIcon={false}>
                {badgeLabel}
              </Badge>
            </div>

            <h3
              className={clsx(
                'm-0 line-clamp-2 text-sm font-bold leading-snug',
                status === 'past' ? 'text-gray-700' : 'text-gray-900'
              )}
            >
              {session.name}
            </h3>

            <SpeakerStack speakers={session.speakers} />

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {session.place?.name && (
                <span className="flex items-center gap-1 text-2xs text-gray-700">
                  <Location01Icon size={11} aria-hidden />
                  {session.place.name}
                </span>
              )}
              <span className="flex items-center gap-1 text-2xs text-gray-700">
                <Clock01Icon size={11} aria-hidden />
                {timeLabel}
              </span>
            </div>
          </div>

          <div className="flex min-h-20 w-[70px] shrink-0 flex-col items-end gap-2 self-stretch overflow-visible">
            <HeartReactionButton
              id={session.id}
              reactableType="Programming"
              reacted={userReaction}
              reactions={localReactions}
              onReactionChange={handleReactionChange}
              likeAriaLabel="Curtir sessão"
              unlikeAriaLabel="Remover curtida da sessão"
            />

            {session.has_rewards && (
              <RewardPill.Group
                points={session.point_reward}
                coins={session.coin_reward}
                className="mt-auto"
              />
            )}

            {canCheckIn && (
              <Button
                variant={status === 'live' ? 'primary' : 'light'}
                size="sm"
                onClick={handleCheckIn}
                showTrailingIcon={false}
                className="w-full justify-center whitespace-nowrap"
              >
                {t('schedule.checkIn.actionShort')}
              </Button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
